import countryData from '@assets/data/countries.json';
import cityData from '@assets/data/worldcities.json';

/**
 * Country (compatible with countries.json)
 */
export interface Country {
    id: string; // iso2
    name: string;
    currency: string;
    regexVaT?: string;
    placeholderVAT?: string;
    vatErrorMessage?: string;
    prefixPhoneNumber?: string;
}

export class Region {
    name: string;
    cities: City[];
    constructor(name: string, cities: City[] | undefined = undefined) {
        this.name = name;
        this.cities = cities || [];
    }
}

/**
 * City (build from what we have in worldcities.json)
 */
export class City {
    iso2: string; // country iso2
    name: string; // city name
    region: string; // city region
    lat: number;
    lng: number;
    id: number;
    /**
     * Constructor
     */
    constructor(iso2: string, name: string, region: string, lat: number, lng: number, id: number) {
        this.iso2 = iso2;
        this.name = name;
        this.region = region;
        this.lat = lat;
        this.lng = lng;
        this.id = id;
    }
}

/**
 * Build country list
 */
export function buildCountryList(): Country[] {
    return countryData;
}

// cached list of City[]
let cityList: City[];

/**
 * Build the city list
 */
export function buildCityList(): City[] {
    if (!cityList) {
        cityList = cityData.map(
            (x) => new City(<string>x[0], <string>x[1], <string>x[2], <number>x[3], <number>x[4], <number>x[5]),
        );
    }
    return cityList;
}

/**
 * Build Lookup table countryIso2->Country
 */
export function buildCountryLUT(): Map<string, Country> {
    const lut = new Map<string, Country>();
    countryData.forEach((country) => lut.set(country.id, country));
    return lut;
}

/**
 * Build Lookup table cityCode->City
 */
export function buildCityLUT(): Map<number, City> {
    const lut = new Map<number, City>();
    buildCityList().forEach((city) => lut.set(city.id, city));
    return lut;
}

/**
 * Build Lookup table countryIso2->City[]
 */
export function buildCountryCitiesLUT(): Map<string, City[]> {
    const lut = new Map<string, City[]>();
    buildCityList().forEach((city) => {
        const entry = lut.get(city.iso2);
        if (entry) {
            entry.push(city);
        } else {
            lut.set(city.iso2, [city]);
        }
    });
    return lut;
}

/**
 * Build Lookup table countryIso2->Region[] with cities
 */
export function buildCountryRegionsLUT(): Map<string, Region[]> {
    const lut = new Map<string, Region[]>();
    buildCityList().forEach((city) => {
        const regions = lut.get(city.iso2);
        if (regions) {
            const region = regions.find((x) => x.name === city.region);
            if (region) {
                region.cities.push(city);
            } else {
                regions.push(new Region(city.region, [city]));
            }
        } else {
            lut.set(city.iso2, [new Region(city.region, [city])]);
        }
    });
    return lut;
}
